/**
 * pages/BibliothequeViewerV2.js
 * The Crowd v2 — Visualiseur d'une bibliotheque.
 *
 * Adaptation v2 de pages/BibliothequeViewer.js, decoupee :
 *  - logique et chargements   -> services/bibliotheque.js
 *  - arborescence             -> components/BibArborescence.js
 *  - tableaux de bord         -> components/BibTableaux.js
 *  - rendu de fiche, bandeaux -> components/BibRenduFiche.js
 *  - modales                  -> components/BibModales.js
 *  - editeur                  -> components/BibEditeurV2.js
 * Cette page orchestre : etat, appels API, enchainements.
 *
 * Comportement repris a l'identique : memes appels API, memes droits
 * (niveau « full » pour editer), memes confirmations natives pour la
 * suppression et le renommage.
 *
 * Plein ecran : arborescence a gauche, contenu a droite sur toute la
 * hauteur sous la barre haute ; chaque zone defile independamment.
 *
 * Droits d'acces : un directeur modifie qui accede a la bibliotheque
 * depuis les actions de la navigation (components/ModaleDroitsBibliothequeV2.js).
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import {
  getBibliothequeConfig,
  getBibliothequeArborescence,
  getBibliothequeFichier,
  updateBibliothequeFichier,
  createBibliothequeFichier,
  deleteBibliothequeFichier,
  uploadFichierBibliotheque,
  uploadImageBibliotheque,
  resolveImagesSas,
  createDossierBibliotheque,
  deleteDossierBibliotheque,
  renameDossierBibliotheque,
} from "../services/api";
import {
  parseFrontMatter,
  findVersions,
  versRelatif,
  prefixeBlob,
  chargerTableauDeBord,
  chargerReferentiel,
  chargerArborescenceGroupee,
} from "../services/bibliotheque";
import IconeV2 from "../components/IconeV2";
import { ArborescenceDossiers, ArborescenceGroupee } from "../components/BibArborescence";
import { TableauDeBord, TableauReferentiel } from "../components/BibTableaux";
import { BandeauFrontMatter, BandeauProcedure, CorpsMarkdown, CorpsHtml } from "../components/BibRenduFiche";
import { ModaleTeleversement, ModaleNouveauDossier, ModaleChoixDossier } from "../components/BibModales";
import BibEditeurV2 from "../components/BibEditeurV2";
import ModaleDroitsBibliothequeV2 from "../components/ModaleDroitsBibliothequeV2";

export default function BibliothequeViewerV2({ bibliotheque, collaborateur, onBack }) {
  const [config, setConfig] = useState(null);
  const [tree, setTree] = useState(null);
  const [selectedPath, setSelectedPath] = useState(null);
  const [selectedName, setSelectedName] = useState(null);
  const [fileContent, setFileContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [showDashboard, setShowDashboard] = useState(true);
  const [showReferentiel, setShowReferentiel] = useState(false);
  const [error, setError] = useState(null);
  const [versions, setVersions] = useState([]);
  const [initLoading, setInitLoading] = useState(true);
  const [modale, setModale] = useState(null); // "upload" | "dossier" | "choix" | "droits" | null
  const [info, setInfo] = useState(null);
  const [creatingDossier, setCreatingDossier] = useState("");
  const [creatingNew, setCreatingNew] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [groupedTree, setGroupedTree] = useState(null);
  const [groupedLoading, setGroupedLoading] = useState(false);
  const [lignesTdb, setLignesTdb] = useState([]);
  const [tdbLoading, setTdbLoading] = useState(true);
  const [lignesRef, setLignesRef] = useState([]);
  const [refLoading, setRefLoading] = useState(true);

  const slug = bibliotheque?.slug;
  const collaborateurId = collaborateur?.id;
  const niveau = bibliotheque?.niveau || (collaborateur?.role === "directeur" ? "full" : "lecture");
  const canEdit = niveau === "full";
  const estDirecteur = collaborateur?.role === "directeur";

  // ── Chargements ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!slug || !collaborateurId) return;
    async function init() {
      setInitLoading(true);
      try {
        const [cfg, arbo] = await Promise.all([
          getBibliothequeConfig(slug, collaborateurId),
          getBibliothequeArborescence(slug, collaborateurId),
        ]);
        setConfig(cfg);
        setTree(arbo.tree);
      } catch (e) {
        setError("Erreur chargement de la bibliothèque.");
      } finally {
        setInitLoading(false);
      }
    }
    init();
  }, [slug, collaborateurId]);

  const rechargerArbre = useCallback(async () => {
    try {
      const arbo = await getBibliothequeArborescence(slug, collaborateurId);
      setTree(arbo.tree);
    } catch {}
  }, [slug, collaborateurId]);

  // Arborescence groupee par front matter
  useEffect(() => {
    if (!config || !tree || config.sidebar_mode !== "groupe_frontmatter") return;
    let annule = false;
    setGroupedLoading(true);
    chargerArborescenceGroupee({ tree, config, slug, collaborateurId }).then((g) => {
      if (!annule) { setGroupedTree(g); setGroupedLoading(false); }
    });
    return () => { annule = true; };
  }, [config, tree, slug, collaborateurId]);

  // Tableau de bord : recharge a chaque affichage, comme l'original
  useEffect(() => {
    if (!showDashboard || !tree || !config) return;
    let annule = false;
    setTdbLoading(true);
    chargerTableauDeBord({ tree, slug, collaborateurId, versioning: config.versioning !== false }).then((lignes) => {
      if (!annule) { setLignesTdb(lignes); setTdbLoading(false); }
    });
    return () => { annule = true; };
  }, [showDashboard, tree, config, slug, collaborateurId]);

  // Referentiel technique : idem
  useEffect(() => {
    if (!showReferentiel || !tree || !config?.referentiel_technique) return;
    let annule = false;
    setRefLoading(true);
    chargerReferentiel({ tree, config, slug, collaborateurId }).then((lignes) => {
      if (!annule) { setLignesRef(lignes); setRefLoading(false); }
    });
    return () => { annule = true; };
  }, [showReferentiel, tree, config, slug, collaborateurId]);

  // ── Navigation ──────────────────────────────────────────────────────────

  const handleSelectFile = useCallback(async (blobPath, fileName) => {
    setSelectedPath(blobPath);
    setSelectedName(fileName || blobPath.split("/").pop());
    setEditing(false);
    setCreatingNew(false);
    setShowDashboard(false);
    setShowReferentiel(false);
    setError(null);
    setLoading(true);
    try {
      const res = await getBibliothequeFichier(slug, versRelatif(slug, blobPath), collaborateurId);
      // Images blob-img: -> URLs SAS fraiches
      const content = res.content.includes("blob-img:")
        ? await resolveImagesSas(res.content, collaborateurId)
        : res.content;
      setFileContent(content);
      if (tree && config?.versioning !== false) setVersions(findVersions(tree, blobPath));
      else setVersions([]);
    } catch {
      setError("Erreur chargement du fichier.");
      setFileContent(null);
    } finally {
      setLoading(false);
    }
  }, [slug, collaborateurId, tree, config]);

  const ouvrirRelatif = (rel) => handleSelectFile(`${prefixeBlob(slug)}${rel}`, rel.split("/").pop());

  const afficherTableauDeBord = () => {
    setShowDashboard(true);
    setShowReferentiel(false);
    setSelectedPath(null);
    setFileContent(null);
    setEditing(false);
    setCreatingNew(false);
  };

  const afficherReferentiel = () => {
    setShowReferentiel(true);
    setShowDashboard(false);
    setSelectedPath(null);
    setFileContent(null);
    setEditing(false);
    setCreatingNew(false);
  };

  // ── Actions ─────────────────────────────────────────────────────────────

  const handleSaveNew = useCallback(async (htmlContent, fileName) => {
    const finalName = creatingDossier ? `${creatingDossier}/${fileName}` : fileName;
    await createBibliothequeFichier(slug, finalName, htmlContent, collaborateurId);
    setCreatingNew(false);
    setCreatingDossier("");
    setFileContent(null);
    setShowDashboard(true);
    await rechargerArbre();
  }, [slug, collaborateurId, creatingDossier, rechargerArbre]);

  const handleSave = useCallback(async (newContent) => {
    await updateBibliothequeFichier(slug, versRelatif(slug, selectedPath), newContent, collaborateurId);
    setFileContent(newContent);
    setEditing(false);
  }, [slug, selectedPath, collaborateurId]);

  const handleDelete = useCallback(async () => {
    if (!selectedPath) return;
    if (!window.confirm(`Supprimer définitivement « ${selectedName} » ?`)) return;
    setDeleting(true);
    try {
      await deleteBibliothequeFichier(slug, versRelatif(slug, selectedPath), collaborateurId);
      setSelectedPath(null);
      setSelectedName(null);
      setFileContent(null);
      setEditing(false);
      setShowDashboard(true);
      await rechargerArbre();
    } catch (e) {
      setError(e.message || "Erreur suppression du fichier.");
    } finally {
      setDeleting(false);
    }
  }, [slug, selectedPath, selectedName, collaborateurId, rechargerArbre]);

  const handleRenameDossier = useCallback(async (chemin, nomActuel) => {
    const nouveauNom = window.prompt("Nouveau nom du dossier :", nomActuel);
    if (!nouveauNom || !nouveauNom.trim() || nouveauNom.trim() === nomActuel) return;
    try {
      await renameDossierBibliotheque(slug, chemin, nouveauNom.trim(), collaborateurId);
      await rechargerArbre();
    } catch (e) {
      setError(e.message || "Erreur renommage du dossier.");
    }
  }, [slug, collaborateurId, rechargerArbre]);

  const handleDeleteDossier = useCallback(async (chemin, nom) => {
    if (!window.confirm(`Supprimer le dossier « ${nom} » et tout son contenu ? Cette action est irréversible.`)) return;
    try {
      await deleteDossierBibliotheque(slug, chemin, collaborateurId);
      if (selectedPath && selectedPath.includes(`/${chemin}/`)) {
        setSelectedPath(null);
        setSelectedName(null);
        setFileContent(null);
        setEditing(false);
        setShowDashboard(true);
      }
      await rechargerArbre();
    } catch (e) {
      setError(e.message || "Erreur suppression du dossier.");
    }
  }, [slug, collaborateurId, selectedPath, rechargerArbre]);

  const validerTeleversement = async (dossier, fichier) => {
    await uploadFichierBibliotheque(slug, dossier, fichier, collaborateurId);
    setModale(null);
    await rechargerArbre();
  };

  const validerNouveauDossier = async (chemin) => {
    await createDossierBibliotheque(slug, chemin, collaborateurId);
    setModale(null);
    await rechargerArbre();
  };

  const validerChoixDossier = (dossier) => {
    setCreatingDossier(dossier);
    setModale(null);
    setCreatingNew(true);
    setSelectedPath(null);
    setFileContent(null);
    setEditing(false);
    setShowDashboard(false);
    setShowReferentiel(false);
  };

  // ── Rendu ───────────────────────────────────────────────────────────────

  const { data: fmData, body } = useMemo(() => {
    if (!fileContent) return { data: {}, body: "" };
    return parseFrontMatter(fileContent);
  }, [fileContent]);

  if (initLoading) {
    return (
      <div className="v2-page v2-page-module">
        <div className="v2-chargement">Chargement…</div>
      </div>
    );
  }

  const vueFiche = !showDashboard && !showReferentiel && !editing && !creatingNew;
  const estHtml = selectedName?.endsWith(".html");

  return (
    <div className="v2-bib">
      {/* ── Colonne gauche : navigation ── */}
      <aside className="v2-bib-nav" aria-label="Navigation de la bibliothèque">
        <div className="v2-bib-nav-entete">
          <button type="button" className="v2-bib-retour" onClick={onBack}>
            <IconeV2 nom="retour" taille={13} /> Bibliothèques
          </button>
          <h1 className="v2-bib-nom">{bibliotheque?.nom}</h1>
          {bibliotheque?.description && <p className="v2-bib-description">{bibliotheque.description}</p>}
        </div>

        <div className="v2-bib-nav-vues">
          <button type="button" className={`v2-bib-vue${showDashboard ? " actif" : ""}`} aria-current={showDashboard ? "true" : undefined} onClick={afficherTableauDeBord}>
            <IconeV2 nom="tableau" /> Tableau de bord
          </button>
          {config?.referentiel_technique && (
            <button type="button" className={`v2-bib-vue${showReferentiel ? " actif" : ""}`} aria-current={showReferentiel ? "true" : undefined} onClick={afficherReferentiel}>
              <IconeV2 nom="referentiel" /> {config.referentiel_technique.label || "Référentiel Technique"}
            </button>
          )}
        </div>

        {canEdit && (
          <div className="v2-bib-nav-actions">
            <button type="button" className="v2-bib-action" onClick={() => setModale("choix")}>
              <IconeV2 nom="plus" /> Nouvelle procédure
            </button>
            <button type="button" className="v2-bib-action" onClick={() => setModale("upload")}>
              <IconeV2 nom="televerser" /> Uploader un fichier
            </button>
            <button type="button" className="v2-bib-action" onClick={() => setModale("dossier")}>
              <IconeV2 nom="dossier" /> Nouveau dossier
            </button>
            {estDirecteur && (
              <button type="button" className="v2-bib-action" onClick={() => { setInfo(null); setModale("droits"); }}>
                Droits d'accès
              </button>
            )}
          </div>
        )}

        <div className="v2-bib-nav-arbo">
          {config?.sidebar_mode === "groupe_frontmatter" ? (
            groupedLoading
              ? <div className="v2-chargement">Chargement…</div>
              : <ArborescenceGroupee groupes={groupedTree} selectedPath={selectedPath} onSelectFile={handleSelectFile} />
          ) : (
            <ArborescenceDossiers
              tree={tree}
              selectedPath={selectedPath}
              onSelectFile={handleSelectFile}
              canEdit={canEdit}
              onRenameDossier={handleRenameDossier}
              onDeleteDossier={handleDeleteDossier}
            />
          )}
        </div>
      </aside>

      {/* ── Colonne droite : contenu ── */}
      <main className="v2-bib-principal">
        {error && <div className="v2-msg v2-msg-erreur v2-bib-erreur">{error}</div>}
        {info && <div className="v2-msg v2-msg-succes v2-bib-erreur" role="status">{info}</div>}

        {showDashboard && tree && config && (
          <div className="v2-bib-defilement">
            <TableauDeBord lignes={lignesTdb} loading={tdbLoading} config={config} onSelectFile={ouvrirRelatif} />
          </div>
        )}

        {showReferentiel && tree && config?.referentiel_technique && (
          <div className="v2-bib-defilement">
            <TableauReferentiel lignes={lignesRef} loading={refLoading} config={config} onSelectFile={ouvrirRelatif} />
          </div>
        )}

        {creatingNew && (
          <BibEditeurV2
            content=""
            filePath=""
            isNew={true}
            slug={slug}
            collaborateurId={collaborateurId}
            collaborateur={collaborateur}
            uploadImageBibliotheque={uploadImageBibliotheque}
            onSave={handleSaveNew}
            onCancel={() => { setCreatingNew(false); setCreatingDossier(""); afficherTableauDeBord(); }}
          />
        )}

        {editing && selectedPath && !creatingNew && (
          <BibEditeurV2
            content={fileContent}
            filePath={selectedName}
            isNew={false}
            slug={slug}
            collaborateurId={collaborateurId}
            collaborateur={collaborateur}
            uploadImageBibliotheque={uploadImageBibliotheque}
            onSave={handleSave}
            onCancel={() => setEditing(false)}
          />
        )}

        {vueFiche && (
          loading ? (
            <div className="v2-chargement">Chargement…</div>
          ) : fileContent ? (
            <>
              <div className="v2-bib-fiche-barre">
                <span className="v2-bib-fiche-nom" title={selectedName}>{selectedName}</span>
                {versions.length > 1 && (
                  <div className="v2-bascule" role="group" aria-label="Versions">
                    {versions.map((v) => (
                      <button
                        key={v.path}
                        type="button"
                        className={`v2-bascule-choix${v.path === selectedPath ? " actif" : ""}`}
                        aria-pressed={v.path === selectedPath}
                        onClick={() => handleSelectFile(v.path, v.name)}
                      >
                        v{v.version}
                      </button>
                    ))}
                  </div>
                )}
                {canEdit && (
                  <div className="v2-bib-fiche-actions">
                    <button type="button" className="v2-btn v2-btn-secondaire" onClick={() => setModale("upload")}>
                      <IconeV2 nom="televerser" /> Uploader
                    </button>
                    <button type="button" className="v2-btn v2-btn-secondaire v2-btn-danger-texte" onClick={handleDelete} disabled={deleting}>
                      <IconeV2 nom="corbeille" /> {deleting ? "Suppression…" : "Supprimer"}
                    </button>
                    <button type="button" className="v2-btn v2-btn-principal" onClick={() => setEditing(true)}>
                      <IconeV2 nom="crayon" /> Éditer
                    </button>
                  </div>
                )}
              </div>

              <div className="v2-bib-defilement">
                {estHtml ? (
                  <>
                    <BandeauProcedure html={fileContent} />
                    <CorpsHtml html={fileContent} />
                  </>
                ) : (
                  <>
                    <BandeauFrontMatter data={fmData} configFields={config?.front_matter} />
                    <CorpsMarkdown body={body} />
                  </>
                )}
              </div>
            </>
          ) : (
            <div className="v2-bib-vide">
              <p className="v2-vide">Sélectionnez un fichier ou consultez le tableau de bord.</p>
            </div>
          )
        )}
      </main>

      {modale === "upload" && <ModaleTeleversement tree={tree} onValider={validerTeleversement} onClose={() => setModale(null)} />}
      {modale === "dossier" && <ModaleNouveauDossier tree={tree} onValider={validerNouveauDossier} onClose={() => setModale(null)} />}
      {modale === "choix" && <ModaleChoixDossier tree={tree} onValider={validerChoixDossier} onClose={() => setModale(null)} />}
      {modale === "droits" && (
        <ModaleDroitsBibliothequeV2
          bibliotheque={bibliotheque}
          collaborateurId={collaborateurId}
          onClose={() => setModale(null)}
          onEnregistre={(nb) => {
            setModale(null);
            setInfo(`Droits enregistrés : ${nb} personne${nb > 1 ? "s ont" : " a"} accès, en plus des directeurs.`);
          }}
        />
      )}
    </div>
  );
}
